import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import {
    LayoutDashboard,
    Package,
    Truck,
    ShoppingCart,
    Warehouse,
    Building2,
    LogOut,
    Menu,
    X,
    ChevronRight,
    PackageCheck,
    Users,
    ArrowLeftRight,
    PanelLeftClose,
    PanelLeft,
    Pin,
    PinOff,
    Shield,
    ShieldCheck,
    ExternalLink,
    FileText,
    ChefHat,
    Wallet,
    ShoppingBag,
    Percent,
    Trash2,
    Factory
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const navItems = [
    { path: '/', label: 'Tableau de bord', icon: LayoutDashboard, permission: 'dashboard_view' },
    { path: '/products', label: 'Produits', icon: Package, permission: 'products_view' },
    { path: '/suppliers', label: 'Fournisseurs', icon: Truck, permission: 'suppliers_view' },
    { path: '/orders', label: 'Commandes', icon: ShoppingCart, anyPermission: ['orders_view', 'consolidation_view', 'transfers_view', 'refacturation_view'] },
    { path: '/inventory', label: 'Stock', icon: Warehouse, permission: 'stock_page_view' },
    { path: '/recipes', label: 'Recettes', icon: ChefHat, permission: 'recipes_view' },
    { path: '/production', label: 'Production', icon: Factory, orPermission: 'production_view', allowedUsers: ['eeginer', 'ddultheo'] },
    { path: '/reception-stats', label: 'Stats Réceptions', icon: PackageCheck, permission: 'stats_view' },
    { path: '/finance', label: 'Suivi financier', icon: Wallet, permission: 'finance_view' },
    { path: '/sales', label: 'Ventes', icon: ShoppingBag, anyPermission: ['sales_financial_view', 'sales_entries_view', 'sales_evolution_view', 'sales_top_view', 'sales_pivot_view', 'sales_import', 'sales_myfood_view', 'sales_myfood_financial_view'],
      children: [
          { path: '/sales', label: 'Fuji Express' },
          { path: '/sales/my-food', label: 'My Food', anyPermission: ['sales_myfood_view', 'sales_myfood_financial_view'] },
          { path: '/sales/concept-lab', label: 'Concept Lab', allowedUsers: ['eeginer', 'ddultheo'] },
      ] },
    { path: '/ratios', label: 'Ratios', icon: Percent, permission: 'ratios_view' },
    { path: '/pertes', label: 'Pertes', icon: Trash2, anyPermission: ['losses_entry', 'losses_cl_entry'] },
    { path: '/sites', label: 'Sites', icon: Building2, permission: 'sites_edit' },
    { path: '/users', label: 'Utilisateurs', icon: Users, permission: 'users_view' },
    { path: '/sync', label: 'Synchronisation', icon: ArrowLeftRight, permission: 'sync_access' },
    { path: '/conformite', label: 'Conformité', icon: ShieldCheck, adminOnly: true },
];

export default function Layout({ children }) {
    const { user, logout, hasPermission } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openSections, setOpenSections] = useState({});
    
    // Sidebar collapse state - load from localStorage
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        return saved === 'true';
    });
    
    // Auto-collapse mode - sidebar collapses automatically and expands on hover
    const [autoCollapse, setAutoCollapse] = useState(() => {
        const saved = localStorage.getItem('sidebarAutoCollapse');
        return saved === 'true';
    });
    
    // Hover state for auto-collapse mode
    const [isHovered, setIsHovered] = useState(false);

    // Trace des pages consultées (journal des connexions)
    useEffect(() => {
        if (user) authAPI.trackPageView(location.pathname).catch(() => {});
    }, [location.pathname, user]);

    // Save preferences to localStorage
    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
    }, [isCollapsed]);
    
    useEffect(() => {
        localStorage.setItem('sidebarAutoCollapse', autoCollapse.toString());
    }, [autoCollapse]);

    const currentPage = navItems.flatMap(item => [item, ...(item.children || []).map(c => ({ ...c, parentLabel: item.label }))])
        .find(item => item.path === location.pathname);

    // Met à jour le titre de l'onglet du navigateur : "<Page> - Sokuzai"
    useEffect(() => {
        const pageLabel = currentPage?.label;
        document.title = pageLabel ? `Sokuzai - ${pageLabel}` : 'Sokuzai';
    }, [currentPage]);
    
    // Determine if sidebar should be expanded
    const shouldExpand = autoCollapse ? isHovered : !isCollapsed;
    const sidebarWidth = shouldExpand ? 'w-64' : 'w-16';
    const mainPadding = shouldExpand ? 'lg:pl-64' : 'lg:pl-16';

    const toggleAutoCollapse = () => {
        if (autoCollapse) {
            // Désactiver le mode auto-masquer -> Épingler le menu (toujours visible)
            setAutoCollapse(false);
            setIsCollapsed(false); // Le menu reste ouvert
        } else {
            // Activer le mode auto-masquer
            setAutoCollapse(true);
            setIsCollapsed(true);
        }
    };

    return (
        <TooltipProvider delayDuration={100}>
            <div className="min-h-screen bg-slate-50">
                {/* Mobile sidebar backdrop */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <aside 
                    className={`
                        fixed top-0 left-0 z-50 h-full ${sidebarWidth} bg-white border-r border-slate-200 
                        transform transition-all duration-200 ease-in-out
                        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    `}
                    onMouseEnter={() => autoCollapse && setIsHovered(true)}
                    onMouseLeave={() => autoCollapse && setIsHovered(false)}
                >
                    <div className="flex flex-col h-full">
                        {/* Logo */}
                        <div className={`flex items-center ${shouldExpand ? 'justify-between' : 'justify-center'} h-16 px-3 border-b border-slate-200`}>
                            <Link to="/" className="flex items-center gap-2">
                                <img
                                    src="/sokuzai-icon.png"
                                    alt="Sokuzai"
                                    className="w-8 h-8 rounded-lg flex-shrink-0 object-contain"
                                />
                                {shouldExpand && (
                                    <span className="font-primary font-bold text-xl text-slate-900 whitespace-nowrap">Sokuzai</span>
                                )}
                            </Link>
                            {shouldExpand && (
                                <button 
                                    className="lg:hidden p-1 hover:bg-slate-100 rounded"
                                    onClick={() => setSidebarOpen(false)}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>

                        {/* Navigation */}
                        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
                            {navItems
                                .filter(item => {
                                    if (item.adminOnly) return user?.role === 'admin';
                                    if (item.allowedUsers) {
                                        if (item.allowedUsers.includes(user?.username)) return true;
                                        return item.orPermission ? (user?.permissions || []).includes(item.orPermission) : false;
                                    }
                                    if (item.permission) return hasPermission(item.permission);
                                    if (item.anyPermission) return item.anyPermission.some(p => hasPermission(p));
                                    return true;
                                })
                                .map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                const sectionOpen = item.children && (openSections[item.path] ?? location.pathname.startsWith(item.path));
                                const toggleSection = () => setOpenSections(s => ({ ...s, [item.path]: !sectionOpen }));
                                
                                const linkContent = (
                                    <div key={item.path}>
                                        {item.children && shouldExpand ? (
                                            <button
                                                type="button"
                                                onClick={toggleSection}
                                                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                                                className={`
                                                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left
                                                    ${isActive 
                                                        ? 'bg-slate-900 text-white' 
                                                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                                    }
                                                `}
                                            >
                                                <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                                                <span className="font-medium whitespace-nowrap flex-1">{item.label}</span>
                                                <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${sectionOpen ? 'rotate-90' : ''} ${isActive ? 'text-slate-300' : 'text-slate-400'}`} />
                                            </button>
                                        ) : (
                                        <Link
                                            to={item.path}
                                            data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                                            className={`
                                                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                                                ${isActive 
                                                    ? 'bg-slate-900 text-white' 
                                                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                                }
                                                ${!shouldExpand ? 'justify-center' : ''}
                                            `}
                                            onClick={() => setSidebarOpen(false)}
                                        >
                                            <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                                            {shouldExpand && (
                                                <span className="font-medium whitespace-nowrap flex-1">{item.label}</span>
                                            )}
                                            {shouldExpand && item.children && (
                                                <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${sectionOpen ? 'rotate-90' : ''} ${isActive ? 'text-slate-300' : 'text-slate-400'}`} />
                                            )}
                                        </Link>
                                        )}
                                        {shouldExpand && sectionOpen && (
                                            <div className="ml-6 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                                                {item.children.filter(c => (!c.allowedUsers || c.allowedUsers.includes(user?.username)) && (!c.permission || hasPermission(c.permission)) && (!c.anyPermission || c.anyPermission.some(p => hasPermission(p)))).map(child => {
                                                    const childActive = location.pathname === child.path;
                                                    return (
                                                        <Link
                                                            key={child.path}
                                                            to={child.path}
                                                            data-testid={`nav-${child.path.replace(/\//g, '-').slice(1)}`}
                                                            className={`
                                                                block px-3 py-1.5 rounded-md text-sm transition-colors
                                                                ${childActive
                                                                    ? 'bg-slate-900 text-white font-medium'
                                                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                                                                }
                                                            `}
                                                            onClick={() => setSidebarOpen(false)}
                                                        >
                                                            {child.label}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                                
                                // Show tooltip when collapsed
                                if (!shouldExpand) {
                                    return (
                                        <Tooltip key={item.path}>
                                            <TooltipTrigger asChild>
                                                {linkContent}
                                            </TooltipTrigger>
                                            <TooltipContent side="right" className="font-medium">
                                                {item.label}
                                            </TooltipContent>
                                        </Tooltip>
                                    );
                                }
                                
                                return linkContent;
                            })}

                            {/* Guide du Sushiman - external link */}
                            {hasPermission('guide_view') && (() => {
                                const guideLink = (
                                    <a
                                        key="guide"
                                        href="/guide"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="nav-guide"
                                        className={`
                                            flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                                            text-slate-600 hover:bg-slate-100 hover:text-slate-900
                                            ${!shouldExpand ? 'justify-center' : ''}
                                        `}
                                    >
                                        <ChefHat className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                                        {shouldExpand && (
                                            <span className="font-medium whitespace-nowrap flex items-center gap-1.5">
                                                Guide Sushiman
                                                <ExternalLink className="w-3 h-3 text-slate-400" />
                                            </span>
                                        )}
                                    </a>
                                );
                                if (!shouldExpand) {
                                    return (
                                        <Tooltip>
                                            <TooltipTrigger asChild>{guideLink}</TooltipTrigger>
                                            <TooltipContent side="right" className="font-medium">Guide du Sushiman</TooltipContent>
                                        </Tooltip>
                                    );
                                }
                                return guideLink;
                            })()}
                        </nav>

                        {/* Collapse toggle button - Desktop only */}
                        <div className="hidden lg:block p-2 border-t border-slate-200 space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleAutoCollapse}
                                        className={`
                                            w-full flex items-center gap-3 px-3 py-2 rounded-lg
                                            text-slate-500 hover:bg-slate-100 hover:text-slate-700
                                            transition-colors
                                            ${!shouldExpand ? 'justify-center' : ''}
                                        `}
                                        data-testid="toggle-sidebar-btn"
                                    >
                                        {autoCollapse ? (
                                            <>
                                                <PinOff className="w-5 h-5 flex-shrink-0" />
                                                {shouldExpand && <span className="text-sm">Épingler le menu</span>}
                                            </>
                                        ) : (
                                            <>
                                                <Pin className="w-5 h-5 flex-shrink-0" />
                                                {shouldExpand && <span className="text-sm">Auto-masquer</span>}
                                            </>
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    {autoCollapse ? 'Épingler le menu (toujours visible)' : 'Auto-masquer le menu'}
                                </TooltipContent>
                            </Tooltip>
                        </div>

                        {/* User section */}
                        <div className={`p-2 border-t border-slate-200 ${!shouldExpand ? 'flex flex-col items-center' : ''}`}>
                            {shouldExpand ? (
                                <>
                                    <div className="flex items-center gap-3 px-3 py-2">
                                        <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-white font-semibold text-sm">
                                                {user?.name?.charAt(0).toUpperCase() || 'U'}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm text-slate-900 truncate">{user?.name}</p>
                                            <p className="text-xs text-slate-500 truncate">{user?.username || user?.email}</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        className="w-full mt-1 justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
                                        onClick={logout}
                                        data-testid="logout-btn"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Déconnexion
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center mb-2">
                                                <span className="text-white font-semibold text-sm">
                                                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                                                </span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            <p className="font-medium">{user?.name}</p>
                                            <p className="text-xs text-slate-400">{user?.username || user?.email}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-slate-600 hover:text-red-600 hover:bg-red-50"
                                                onClick={logout}
                                                data-testid="logout-btn"
                                            >
                                                <LogOut className="w-4 h-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            Déconnexion
                                        </TooltipContent>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Main content */}
                <div className={`${mainPadding} transition-all duration-200 min-w-0`}>
                    {/* Header */}
                    <header className="glass-header sticky top-0 z-30 h-16 flex items-center justify-between px-6">
                        <div className="flex items-center gap-4">
                            <button 
                                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
                                onClick={() => setSidebarOpen(true)}
                                data-testid="mobile-menu-btn"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <span>Sokuzai</span>
                                <ChevronRight className="w-4 h-4" />
                                <span className="font-medium text-slate-900">{currentPage?.parentLabel ? `${currentPage.parentLabel} ${currentPage.label}` : (currentPage?.label || 'Page')}</span>
                            </div>
                        </div>
                    </header>

                    {/* Page content */}
                    <main className="p-6 lg:p-8">
                        {children}
                    </main>
                </div>
            </div>
        </TooltipProvider>
    );
}
